import React, { useState, useEffect, useCallback } from 'react';
import {
    Receipt, Plus, Trash2, FileText, Check, X, Euro, Package, AlertCircle, Printer, RotateCcw,
    Pencil, Percent
} from 'lucide-react';
import api from '../../services/api';
import { fatturazioneAPI, pacchettiAPI } from '../../services/fatturazioneApi';
import '../../styles/FatturazioneTab.css';
import { useAuthStore } from '../../store/useStore';

/**
 * FatturazioneTab — la cassa, dentro la scheda del paziente.
 *
 * Il paziente e' al banco: si selezionano le prestazioni eseguite,
 * si aggiunge quel che manca, si emette e si incassa.
 *
 * Le regole non sono qui: il backend rifiuta cio' che non si puo'
 * fare e questo componente mostra il messaggio che riceve.
 *
 * Props: pazienteId
 */

const euro = (v) =>
    (parseFloat(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const dataIta = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    return isNaN(d) ? '-' : d.toLocaleDateString('it-IT');
};

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

const FatturazioneTab = ({ pazienteId }) => {
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    const [daFatturare, setDaFatturare] = useState([]);
    const [selezionate, setSelezionate] = useState([]);
    const [documenti, setDocumenti] = useState([]);
    const [documentiTotale, setDocumentiTotale] = useState(0);
    const [pacchetti, setPacchetti] = useState([]);
    /* Vendere un pacchetto e fatturarlo (Alex, 24/09/2026; migration 238).
       La vendita e' dell'amministrazione e della segreteria, come la rotta. */
    const ruolo = useAuthStore((state) => state.user?.ruolo);
    const vende = ruolo === 'admin' || ruolo === 'segreteria';
    const [vendita, setVendita] = useState(null);   // { prestazione_id, descrizione, numero_sedute, prezzo_totale }
    const [importoPacchetto, setImportoPacchetto] = useState({});   // id -> testo

    const [bozza, setBozza] = useState(null);       // { fattura, righe }
    const [medici, setMedici] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [metodi, setMetodi] = useState([]);

    const [pannello, setPannello] = useState(null); // 'catalogo' | 'libera' | 'incasso'
    const [ricerca, setRicerca] = useState('');
    const [medicoScelto, setMedicoScelto] = useState('');
    const [rigaLibera, setRigaLibera] = useState({
        descrizione: '', prezzo_unitario: '', quantita: 1, sconto: '', aliquota_id: '',
    });
    const [aliquote, setAliquote] = useState([]);
    /* Quante unita' per la voce di catalogo che si sta aggiungendo. Cinque
       cornetti sono una riga per cinque, non cinque righe da uno. */
    const [quantitaCatalogo, setQuantitaCatalogo] = useState(1);
    // Sconto sul totale, in corso di digitazione
    const [scontoFinale, setScontoFinale] = useState('');
    // Riga in modifica: { id, quantita, sconto }
    const [rigaInModifica, setRigaInModifica] = useState(null);
    const [incasso, setIncasso] = useState({ importo: '', metodo_pagamento: '' });
    const [inCorso, setInCorso] = useState(false);
    const [confermaStampa, setConfermaStampa] = useState(null);   // id documento
    const [notaCredito, setNotaCredito] = useState(null);         // { fatturaId, righe }
    const [righeStorno, setRigheStorno] = useState([]);
    const [motivoStorno, setMotivoStorno] = useState('');
    const [nonMostrare, setNonMostrare] = useState(false);

    /* ---------- caricamento ---------- */

    const caricaTutto = useCallback(async () => {
        try {
            setCaricamento(true);
            setErrore(null);
            const [pr, doc, pac] = await Promise.all([
                fatturazioneAPI.daFatturare(pazienteId),
                /* Solo i documenti di questo paziente: il filtro lo fa il
                   server. Senza, arrivavano gli ultimi cinquanta di tutti. */
                fatturazioneAPI.elenco({ paziente_id: pazienteId, per_pagina: 200 }),
                pacchettiAPI.perPaziente(pazienteId),
            ]);
            setDaFatturare(pr.data.data.prestazioni || []);
            const tutti = doc.data.data.fatture || [];
            setDocumenti(tutti);
            setDocumentiTotale(doc.data.data.totale || tutti.length);
            setPacchetti(pac.data.data.pacchetti || []);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare i dati di fatturazione'));
        } finally {
            setCaricamento(false);
        }
    }, [pazienteId]);

    useEffect(() => { caricaTutto(); }, [caricaTutto]);

    useEffect(() => {
        const carica = async () => {
            try {
                const [m, p, mp] = await Promise.all([
                    api.get('/medici'),
                    api.get('/prestazioni', { params: { soloAttive: 'true' } }),
                    api.get('/fatturazione/metodi-pagamento'),
                ]);
                setMedici(m.data.data.medici || m.data.data || []);
                setCatalogo(p.data.data.prestazioni || []);
                setMetodi(mp.data.data.metodi || []);
                try {
                    const al = await api.get('/prestazioni/aliquote');
                    setAliquote(al.data.data.aliquote || []);
                } catch (e) { /* si ripiega sull'ordinaria */ }
            } catch (e) {
                console.error('Caricamento anagrafiche di supporto:', e);
            }
        };
        carica();
    }, []);

    const ricaricaBozza = async (id) => {
        const r = await fatturazioneAPI.dettaglio(id, { paziente_id: pazienteId });
        setBozza({
            fattura: r.data.data.fattura,
            righe: r.data.data.righe || [],
            incassi: r.data.data.incassi || [],
            bollo_previsto: r.data.data.bollo_previsto || 0,
        });
        // Il campo mostra quello che c'e' in archivio, non l'ultima digitazione
        const sc = parseFloat(r.data.data.fattura?.sconto_finale || 0);
        setScontoFinale(sc > 0 ? String(sc) : '');
    };

    /* ---------- pacchetti ---------- */

    const vendiPacchetto = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const voce = catalogo.find((v) => String(v.id) === String(vendita.prestazione_id));
            await pacchettiAPI.vendi({
                paziente_id: pazienteId,
                prestazione_id: voce ? voce.id : null,
                descrizione: vendita.descrizione,
                numero_sedute: parseInt(vendita.numero_sedute, 10),
                prezzo_totale: parseFloat(String(vendita.prezzo_totale).replace(',', '.')),
                /* Il listino della voce serve al recesso: le sedute eseguite
                   si rivalutano a prezzo pieno. */
                prezzo_listino_unitario: voce && voce.prezzo_indicativo ? Number(voce.prezzo_indicativo) : null,
            });
            setVendita(null);
            await caricaTutto();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella vendita del pacchetto'));
        } finally {
            setInCorso(false);
        }
    };

    /* Una riga del pacchetto: nella bozza aperta, se c'e' (la fattura puo'
       portare anche altre righe), altrimenti in un documento nuovo. La
       rata si scrive nel campo accanto; vuoto = tutto quello che resta. */
    const fatturaPacchetto = async (p) => {
        try {
            setInCorso(true);
            setErrore(null);
            const testo = String(importoPacchetto[p.id] ?? '').trim();
            const riga = { pacchetto_id: p.id, ...(testo ? { importo: testo } : {}) };
            let fatturaId;
            if (bozza && bozza.fattura.stato === 'bozza' && bozza.fattura.tipo_documento === 'fattura') {
                fatturaId = bozza.fattura.id;
                await fatturazioneAPI.aggiungiRiga(fatturaId, riga);
            } else {
                const s = await fatturazioneAPI.soggettoDaPaziente(pazienteId);
                const f = await fatturazioneAPI.creaDocumento({ soggetto_id: s.data.data.soggetto.id, righe: [riga] });
                fatturaId = f.data.data.fattura.id;
            }
            setImportoPacchetto((x) => ({ ...x, [p.id]: '' }));
            await ricaricaBozza(fatturaId);
            await caricaTutto();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella fattura del pacchetto'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- creazione documento ---------- */

    const creaDocumento = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const s = await fatturazioneAPI.soggettoDaPaziente(pazienteId);
            const soggettoId = s.data.data.soggetto.id;
            /* IL DOCUMENTO INTERO IN UNA RICHIESTA (fase 2 del riordino, 23
               settembre 2026). Prima: la bozza, poi una richiesta per riga -
               con tre prestazioni nella stessa visita, una terza rifiutata
               lasciava una bozza con due righe. Adesso il server crea bozza e
               righe in una transazione sola: o tutto, o niente, e il
               messaggio dice quale riga non e' passata. */
            const f = await fatturazioneAPI.creaDocumento({
                soggetto_id: soggettoId,
                righe: selezionate.map((id) => ({ visita_prestazione_id: id })),
            });
            const fatturaId = f.data.data.fattura.id;
            setSelezionate([]);
            await ricaricaBozza(fatturaId);
            await caricaTutto();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella creazione del documento'));
        } finally {
            setInCorso(false);
        }
    };

    const salvaSconto = async () => {
        try {
            setErrore(null);
            const v = parseFloat(String(scontoFinale).replace(',', '.')) || 0;
            await fatturazioneAPI.impostaSconto(bozza.fattura.id, v);
            await ricaricaBozza(bozza.fattura.id);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'impostazione dello sconto'));
        }
    };

    const salvaRiga = async () => {
        if (!rigaInModifica) return;
        try {
            setErrore(null);
            /* NIENTE «|| 1» sulla quantita': uno zero scritto per sbaglio
               diventava un uno, e la riga si fatturava a prezzo pieno. Il
               server rifiuta con un messaggio, e il messaggio si vede.
               Sullo sconto il «|| 0» invece va bene: zero sconto e' la
               risposta giusta a un campo vuoto. */
            await fatturazioneAPI.modificaRiga(bozza.fattura.id, rigaInModifica.id, {
                quantita: String(rigaInModifica.quantita ?? '').trim(),
                sconto: parseFloat(String(rigaInModifica.sconto).replace(',', '.')) || 0,
            });
            setRigaInModifica(null);
            await ricaricaBozza(bozza.fattura.id);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella modifica della riga'));
        }
    };

    const aggiungiDaCatalogo = async (voce) => {
        try {
            setErrore(null);
            const q = parseFloat(String(quantitaCatalogo).replace(',', '.')) || 1;
            const corpo = { prestazione_id: voce.id, quantita: q };
            if (voce.tipologia === 'servizio') {
                if (!medicoScelto) {
                    setErrore('Scegli il medico che ha eseguito la prestazione.');
                    return;
                }
                /* Solo fra gli abilitati alla voce, se ne ha (decisione del
                   23 settembre 2026): lo controlla il server, qui si dice
                   prima chi sono invece di aspettare il rifiuto. */
                const abilitati = voce.medici || [];
                if (abilitati.length && !abilitati.some((m) => String(m.id) === String(medicoScelto))) {
                    setErrore('Questa voce la eseguono solo: '
                        + abilitati.map((m) => `${m.titolo || ''} ${m.cognome}`.trim()).join(', ') + '.');
                    return;
                }
                corpo.medico_id = parseInt(medicoScelto, 10);
            }
            await fatturazioneAPI.aggiungiRiga(bozza.fattura.id, corpo);
            await ricaricaBozza(bozza.fattura.id);
            setPannello(null);
            setRicerca('');
            setQuantitaCatalogo(1);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'aggiunta della voce'));
        }
    };

    const aggiungiLibera = async () => {
        try {
            setErrore(null);
            await fatturazioneAPI.aggiungiRiga(bozza.fattura.id, {
                descrizione: rigaLibera.descrizione,
                prezzo_unitario: parseFloat(String(rigaLibera.prezzo_unitario).replace(',', '.')),
                quantita: parseFloat(String(rigaLibera.quantita).replace(',', '.')) || 1,
                sconto: parseFloat(String(rigaLibera.sconto).replace(',', '.')) || 0,
                aliquota_id: rigaLibera.aliquota_id ? parseInt(rigaLibera.aliquota_id, 10) : null,
            });
            setRigaLibera({
                descrizione: '', prezzo_unitario: '', quantita: 1, sconto: '',
                aliquota_id: rigaLibera.aliquota_id,
            });
            setPannello(null);
            await ricaricaBozza(bozza.fattura.id);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'aggiunta della riga'));
        }
    };

    const eliminaRiga = async (rigaId) => {
        try {
            await fatturazioneAPI.eliminaRiga(bozza.fattura.id, rigaId);
            await ricaricaBozza(bozza.fattura.id);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'eliminazione'));
        }
    };

    const cambiaBollo = async (escluso) => {
        try {
            await fatturazioneAPI.impostaBollo(bozza.fattura.id, escluso);
            await ricaricaBozza(bozza.fattura.id);
        } catch (e) {
            setErrore(messaggio(e, 'Errore sul bollo'));
        }
    };

    const emetti = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await fatturazioneAPI.emetti(bozza.fattura.id);
            setAvviso(r.data.message);
            await ricaricaBozza(bozza.fattura.id);
            await caricaTutto();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'emissione'));
        } finally {
            setInCorso(false);
        }
    };

    const registraIncasso = async () => {
        try {
            setErrore(null);
            await fatturazioneAPI.incassa(bozza.fattura.id, {
                importo: parseFloat(incasso.importo),
                metodo_pagamento: incasso.metodo_pagamento,
            });
            setIncasso({ importo: '', metodo_pagamento: '' });
            setPannello(null);
            await ricaricaBozza(bozza.fattura.id);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella registrazione dell\'incasso'));
        }
    };

    // Il PDF si scarica con axios, che porta il token nell'header:
    // window.open aprirebbe una richiesta anonima e il backend
    // risponderebbe "Token non fornito".
    /**
     * L'avviso compare una volta al giorno per utente: la chiave
     * contiene la data, quindi si azzera da sola a mezzanotte.
     */
    const chiaveAvviso = () => {
        let utente = 'x';
        try {
            utente = JSON.parse(localStorage.getItem('user') || '{}').id || 'x';
        } catch (e) { /* utente sconosciuto: l'avviso si mostra */ }
        return `ftz-avviso-stampa-${utente}-${new Date().toISOString().slice(0, 10)}`;
    };

    const chiediStampa = (id) => {
        if (localStorage.getItem(chiaveAvviso()) === 'no') {
            stampaDefinitiva(id);
            return;
        }
        setNonMostrare(false);
        setConfermaStampa(id);
    };

    const stampaDefinitiva = async (id) => {
        try {
            setErrore(null);
            setInCorso(true);
            if (nonMostrare) localStorage.setItem(chiaveAvviso(), 'no');
            setConfermaStampa(null);
            const r = await api.post(`/fatturazione/${id}/stampa`);
            setAvviso(r.data.message);
            await apriPdf(id);
            await ricaricaBozza(id);
            await caricaTutto();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella stampa'));
        } finally {
            setInCorso(false);
        }
    };

    const apriNotaCredito = () => {
        // Di norma si storna tutto: chi vuole il parziale toglie le spunte
        setRigheStorno(bozza.righe.filter((r) => !r.stornata_da_id).map((r) => r.id));
        setMotivoStorno('');
        setNotaCredito(bozza.fattura.id);
    };

    const creaNotaCredito = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const tutte = bozza.righe.filter((r) => !r.stornata_da_id).length;
            const r = await api.post(`/fatturazione/${notaCredito}/nota-credito`, {
                righe_ids: righeStorno.length === tutte ? undefined : righeStorno,
                motivo: motivoStorno || undefined,
            });
            setNotaCredito(null);
            setAvviso(r.data.message);
            await ricaricaBozza(r.data.data.nota_credito.id);
            await caricaTutto();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella creazione della nota di credito'));
        } finally {
            setInCorso(false);
        }
    };

    const apriPdf = async (id) => {
        try {
            setErrore(null);
            const r = await api.get(`/fatturazione/${id}/pdf`, {
                responseType: 'blob',
                params: { paziente_id: pazienteId },
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const finestra = window.open(url, '_blank');
            if (!finestra) {
                // Se il browser blocca la finestra, si scarica il file
                const a = document.createElement('a');
                a.href = url;
                a.download = `documento_${id}.pdf`;
                a.click();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile aprire il documento'));
        }
    };

    /* ---------- viste ---------- */

    if (caricamento) return <div className="ftz-attesa">Caricamento…</div>;

    const emessa = bozza && bozza.fattura.stato !== 'bozza';
    // Finche' non e' stampata il documento resta correggibile,
    // anche se ha gia' numero e data.
    const bloccata = bozza && !!bozza.fattura.stampata_il;
    const catalogoFiltrato = ricerca.length >= 2
        ? catalogo.filter((v) =>
            (v.nome || '').toLowerCase().includes(ricerca.toLowerCase()) ||
            (v.codice || '').toLowerCase().includes(ricerca.toLowerCase())
          ).slice(0, 25)
        : [];
    /* Le prestazioni da fatturare raggruppate per visita (visita_id ->
       id delle righe): una visita con piu' prestazioni si fattura intera. */
    const perVisita = daFatturare.reduce((m, p) => {
        (m[p.visita_id] = m[p.visita_id] || []).push(p.id);
        return m;
    }, {});

    return (
        <div className="ftz-wrap">
            {errore && (
                <div className="ftz-errore">
                    <AlertCircle size={16} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={16} /></button>
                </div>
            )}
            {avviso && (
                <div className="ftz-avviso">
                    <Check size={16} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={16} /></button>
                </div>
            )}

            {/* ---------- documento in lavorazione ---------- */}
            {bozza && (
                <div className="ftz-card ftz-card--attivo">
                    <div className="ftz-card-head">
                        <span>
                            <Receipt size={16} />
                            {emessa
                                ? ` Fattura n. ${bozza.fattura.numero}/${bozza.fattura.anno} del ${dataIta(bozza.fattura.data_documento)}`
                                : ' Documento in preparazione'}
                        </span>
                        <button className="ftz-chiudi" onClick={() => setBozza(null)} title="Chiudi">
                            <X size={16} />
                        </button>
                    </div>

                    <table className="ftz-tabella">
                        <thead>
                            <tr>
                                <th>Descrizione</th>
                                <th className="num">Q.tà</th>
                                <th className="num">Prezzo</th>
                                <th className="num">Sconto</th>
                                <th className="num">IVA</th>
                                <th className="num">Importo</th>
                                {!bloccata && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {bozza.righe.map((r) => (
                                <tr key={r.id}>
                                    <td className={r.stornata_da_id ? 'ftz-stornata' : undefined}>
                                        {r.descrizione}
                                        {r.stornata_da_id && (
                                            <span className="ftz-data">stornata</span>
                                        )}
                                        {r.medico_cognome && (
                                            <span className="ftz-medico">
                                                {r.medico_titolo} {r.medico_cognome}
                                            </span>
                                        )}
                                    </td>
                                    <td className="num">
                                        {rigaInModifica?.id === r.id ? (
                                            <input className="ftz-mini" type="number" step="0.01" min="0.01"
                                                value={rigaInModifica.quantita}
                                                onChange={(e) => setRigaInModifica({ ...rigaInModifica, quantita: e.target.value })} />
                                        ) : parseFloat(r.quantita).toLocaleString('it-IT', { maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="num">{euro(r.prezzo_unitario)}</td>
                                    <td className="num">
                                        {rigaInModifica?.id === r.id ? (
                                            <input className="ftz-mini" type="number" step="0.01" min="0"
                                                value={rigaInModifica.sconto}
                                                onChange={(e) => setRigaInModifica({ ...rigaInModifica, sconto: e.target.value })} />
                                        ) : (parseFloat(r.sconto) > 0 ? euro(r.sconto) : '—')}
                                    </td>
                                    <td className="num">
                                        {parseFloat(r.iva_percentuale) > 0
                                            ? `${parseFloat(r.iva_percentuale).toFixed(0)}%`
                                            : 'es.'}
                                    </td>
                                    <td className="num">{euro(r.imponibile)}</td>
                                    {!bloccata && (
                                        <td className="num ftz-azioni-riga">
                                            {rigaInModifica?.id === r.id ? (
                                                <>
                                                    <button className="ftz-icona" onClick={salvaRiga} title="Salva">
                                                        <Check size={16} />
                                                    </button>
                                                    <button className="ftz-icona" onClick={() => setRigaInModifica(null)} title="Annulla">
                                                        <X size={16} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Correggere quantita' e sconto senza cancellare
                                                        e rifare: su una riga nata da una prestazione
                                                        eseguita, rifarla vorrebbe dire rifare anche
                                                        quella. */}
                                                    <button className="ftz-icona"
                                                        onClick={() => setRigaInModifica({
                                                            id: r.id,
                                                            quantita: parseFloat(r.quantita),
                                                            sconto: parseFloat(r.sconto) || 0,
                                                        })}
                                                        title="Quantità e sconto">
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button className="ftz-icona" onClick={() => eliminaRiga(r.id)} title="Elimina">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {!bozza.righe.length && (
                                <tr><td colSpan={7} className="ftz-vuoto">Nessuna voce</td></tr>
                            )}
                            {parseFloat(bozza.fattura.bollo || bozza.bollo_previsto || 0) > 0 && (
                                <tr className="ftz-riga-bollo">
                                    <td>
                                        Imposta di bollo assolta in modo virtuale
                                        {!emessa && (
                                            <span className="ftz-data">verra' applicata all'emissione</span>
                                        )}
                                    </td>
                                    <td className="num">1</td>
                                    <td className="num">{euro(bozza.fattura.bollo || bozza.bollo_previsto)}</td>
                                    <td className="num">—</td>
                                    <td className="num">N1</td>
                                    <td className="num">{euro(bozza.fattura.bollo || bozza.bollo_previsto)}</td>
                                    {!bloccata && <td />}
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {!bloccata && (
                        <div className="ftz-sconto">
                            <Percent size={15} />
                            <label htmlFor="ftz-sconto-finale">Sconto sul totale</label>
                            <input id="ftz-sconto-finale" className="ftz-mini" type="number"
                                step="0.01" min="0" placeholder="0,00"
                                value={scontoFinale}
                                onChange={(e) => setScontoFinale(e.target.value)}
                                onBlur={salvaSconto}
                                onKeyDown={(e) => { if (e.key === 'Enter') salvaSconto(); }} />
                            <span className="ftz-nota-inline">
                                si ripartisce sulle basi imponibili, così l&apos;IVA resta coerente
                            </span>
                        </div>
                    )}

                    <div className="ftz-totali">
                        {parseFloat(bozza.fattura.sconto_finale || 0) > 0 && (
                            <div className="ftz-sconto-riga">
                                <span>Sconto</span><b>− {euro(bozza.fattura.sconto_finale)}</b>
                            </div>
                        )}
                        <div><span>Imponibile</span><b>{euro(bozza.fattura.totale_imponibile)}</b></div>
                        <div><span>IVA</span><b>{euro(bozza.fattura.totale_iva)}</b></div>
                        {parseFloat(bozza.fattura.bollo) > 0 && (
                            <div><span>Bollo</span><b>{euro(bozza.fattura.bollo)}</b></div>
                        )}
                        <div className="ftz-totale">
                            <span>Totale</span><b>{euro(bozza.fattura.totale)} €</b>
                        </div>
                    </div>

                    {!bloccata && (
                        <>
                            <div className="ftz-azioni">
                                <button className="ftz-btn" onClick={() => setPannello('catalogo')}>
                                    <Plus size={16} /> Da catalogo
                                </button>
                                <button className="ftz-btn" onClick={() => setPannello('libera')}>
                                    <Plus size={16} /> Voce libera
                                </button>
                                <label className="ftz-check">
                                    <input
                                        type="checkbox"
                                        checked={!!bozza.fattura.bollo_escluso}
                                        onChange={(e) => cambiaBollo(e.target.checked)}
                                    />
                                    Escludi bollo
                                </label>
                                <button
                                    className="ftz-btn ftz-btn--principale"
                                    onClick={emetti}
                                    disabled={inCorso || !bozza.righe.length}
                                >
                                    <Check size={16} /> Emetti
                                </button>
                            </div>
                            <p className="ftz-nota">
                                Il numero viene assegnato all'emissione. Dopo, il documento non si modifica più.
                            </p>
                        </>
                    )}

                    {emessa && (
                        <div className="ftz-azioni">
                            <button className="ftz-btn" onClick={() => apriPdf(bozza.fattura.id)}>
                                <FileText size={16} /> Anteprima
                            </button>
                            {!bozza.fattura.stampata_il && (
                                <button
                                    className="ftz-btn"
                                    onClick={() => chiediStampa(bozza.fattura.id)}
                                    disabled={inCorso}
                                >
                                    <Printer size={16} /> Stampa definitiva
                                </button>
                            )}
                            <button className="ftz-btn" onClick={() => {
                                setIncasso({
                                    importo: bozza.fattura.totale,
                                    metodo_pagamento: metodi[0]?.codice || '',
                                });
                                setPannello('incasso');
                            }}>
                                <Euro size={16} /> Registra incasso
                            </button>
                            {bozza.fattura.stampata_il
                             && bozza.fattura.tipo_documento === 'fattura'
                             && bozza.righe.some((r) => !r.stornata_da_id) && (
                                <button className="ftz-btn" onClick={apriNotaCredito}>
                                    <RotateCcw size={16} /> Nota di credito
                                </button>
                            )}
                        </div>
                    )}
                    {emessa && !bozza.fattura.stampata_il && (
                        <p className="ftz-nota">
                            Il documento non e' ancora stato stampato: si puo' ancora correggere
                            riaprendolo dall'elenco. Dopo la stampa servira' una nota di credito.
                        </p>
                    )}
                    {emessa && bozza.fattura.stampata_il && (
                        <p className="ftz-nota">
                            Stampato il {dataIta(bozza.fattura.stampata_il)}: non e' piu' modificabile.
                        </p>
                    )}

                    {bozza.incassi && bozza.incassi.length > 0 && (
                        <div className="ftz-incassi">
                            {bozza.incassi.map((i) => (
                                <div key={i.id}>
                                    {dataIta(i.data_incasso)} · {i.metodo_descrizione} · {euro(i.importo)} €
                                    {!i.pagamento_tracciato && (
                                        <span className="ftz-nontracciato">non tracciato</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ---------- pannelli ---------- */}
            {pannello === 'catalogo' && (
                <div className="ftz-pannello">
                    <div className="ftz-pannello-head">
                        Aggiungi dal catalogo
                        <button onClick={() => setPannello(null)}><X size={16} /></button>
                    </div>
                    <div className="ftz-form-riga">
                        <input
                            autoFocus
                            placeholder="Cerca per nome o codice…"
                            value={ricerca}
                            onChange={(e) => setRicerca(e.target.value)}
                        />
                        {/* Le unita' si scelgono PRIMA di toccare la voce: cinque
                            cornetti sono una riga per cinque, non cinque righe da
                            uno. Restano quelle finche' non si chiude il pannello. */}
                        <label className="ftz-qta">
                            Unità
                            <input className="ftz-mini" type="number" step="0.01" min="0.01"
                                value={quantitaCatalogo}
                                onChange={(e) => setQuantitaCatalogo(e.target.value)} />
                        </label>
                        <select value={medicoScelto} onChange={(e) => setMedicoScelto(e.target.value)}>
                            <option value="">Medico esecutore…</option>
                            {medici.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.titolo} {m.cognome} {m.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                    <p className="ftz-nota">
                        Il medico serve solo per le prestazioni: i prodotti non generano spettanza.
                        Per una voce con medici abilitati si sceglie uno di loro. La prestazione si
                        registra con la data di oggi.
                    </p>
                    <div className="ftz-risultati">
                        {catalogoFiltrato.map((v) => (
                            <button key={v.id} className="ftz-risultato" onClick={() => aggiungiDaCatalogo(v)}>
                                <span className={`ftz-tipo ftz-tipo--${v.tipologia}`}>
                                    {v.tipologia === 'bene' ? 'Prodotto' : 'Prestazione'}
                                </span>
                                <span className="ftz-risultato-nome">{v.nome}</span>
                                <span className="ftz-risultato-prezzo">{euro(v.prezzo_indicativo)} €</span>
                            </button>
                        ))}
                        {ricerca.length >= 2 && !catalogoFiltrato.length && (
                            <p className="ftz-vuoto">Nessuna voce trovata</p>
                        )}
                    </div>
                </div>
            )}

            {pannello === 'libera' && (
                <div className="ftz-pannello">
                    <div className="ftz-pannello-head">
                        Voce libera
                        <button onClick={() => setPannello(null)}><X size={16} /></button>
                    </div>
                    <div className="ftz-form-riga">
                        <input
                            autoFocus
                            placeholder="Descrizione"
                            value={rigaLibera.descrizione}
                            onChange={(e) => setRigaLibera({ ...rigaLibera, descrizione: e.target.value })}
                        />
                        <input
                            type="number" step="0.01" placeholder="Prezzo"
                            value={rigaLibera.prezzo_unitario}
                            onChange={(e) => setRigaLibera({ ...rigaLibera, prezzo_unitario: e.target.value })}
                        />
                        <input
                            className="ftz-mini" type="number" step="0.01" min="0.01" placeholder="Unità"
                            title="Unità"
                            value={rigaLibera.quantita}
                            onChange={(e) => setRigaLibera({ ...rigaLibera, quantita: e.target.value })}
                        />
                        <input
                            className="ftz-mini" type="number" step="0.01" min="0" placeholder="Sconto"
                            title="Sconto sulla riga, in euro"
                            value={rigaLibera.sconto}
                            onChange={(e) => setRigaLibera({ ...rigaLibera, sconto: e.target.value })}
                        />
                        {/* L'aliquota va scelta: senza, la riga libera veniva
                            rifiutata dal database perche' un'IVA a zero senza
                            natura non e' un'esenzione, e' un dato incompleto.
                            Vuoto = aliquota ordinaria. */}
                        <select value={rigaLibera.aliquota_id}
                            onChange={(e) => setRigaLibera({ ...rigaLibera, aliquota_id: e.target.value })}>
                            <option value="">IVA ordinaria</option>
                            {aliquote.map((a) => (
                                <option key={a.id} value={a.id}>{a.descrizione_breve}</option>
                            ))}
                        </select>
                        <button className="ftz-btn ftz-btn--principale" onClick={aggiungiLibera}>
                            Aggiungi
                        </button>
                    </div>
                    <p className="ftz-nota">
                        Per le prestazioni conviene creare la voce a catalogo: la riga libera non genera
                        spettanza e non porta il tipo di spesa per il Sistema TS.
                    </p>
                </div>
            )}

            {pannello === 'incasso' && (
                <div className="ftz-pannello">
                    <div className="ftz-pannello-head">
                        Registra incasso
                        <button onClick={() => setPannello(null)}><X size={16} /></button>
                    </div>
                    <div className="ftz-form-riga">
                        <input
                            type="number" step="0.01" placeholder="Importo"
                            value={incasso.importo}
                            onChange={(e) => setIncasso({ ...incasso, importo: e.target.value })}
                        />
                        <select
                            value={incasso.metodo_pagamento}
                            onChange={(e) => setIncasso({ ...incasso, metodo_pagamento: e.target.value })}
                        >
                            <option value="">Metodo…</option>
                            {metodi.map((m) => (
                                <option key={m.codice} value={m.codice}>
                                    {m.descrizione}{!m.tracciato ? ' (non tracciato)' : ''}
                                </option>
                            ))}
                        </select>
                        <button className="ftz-btn ftz-btn--principale" onClick={registraIncasso}>
                            Registra
                        </button>
                    </div>
                    <p className="ftz-nota">
                        Il contante fa perdere la detrazione al paziente, tranne per farmaci e
                        dispositivi medici.
                    </p>
                </div>
            )}

            {confermaStampa && (
                <div className="ftz-modale-sfondo" onClick={() => setConfermaStampa(null)}>
                    <div className="ftz-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="ftz-modale-head">
                            <AlertCircle size={18} /> Stampa definitiva
                        </div>
                        <p>
                            Una volta stampata, la fattura non puo' piu' essere modificata.
                            Per correggerla servira' una nota di credito.
                        </p>
                        <label className="ftz-check">
                            <input
                                type="checkbox"
                                checked={nonMostrare}
                                onChange={(e) => setNonMostrare(e.target.checked)}
                            />
                            Non mostrare piu' oggi
                        </label>
                        <div className="ftz-modale-azioni">
                            <button className="ftz-btn" onClick={() => setConfermaStampa(null)}>
                                Annulla
                            </button>
                            <button
                                className="ftz-btn ftz-btn--principale"
                                onClick={() => stampaDefinitiva(confermaStampa)}
                                disabled={inCorso}
                            >
                                <Printer size={16} /> Stampa
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {notaCredito && (
                <div className="ftz-modale-sfondo" onClick={() => setNotaCredito(null)}>
                    <div className="ftz-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="ftz-modale-head">
                            <RotateCcw size={18} /> Nota di credito
                        </div>
                        <p>
                            Scegli cosa stornare. Le prestazioni stornate tornano disponibili
                            e la spettanza al medico viene annullata.
                        </p>
                        <div className="ftz-storno-righe">
                            {bozza.righe.filter((r) => !r.stornata_da_id).map((r) => (
                                <label key={r.id} className="ftz-storno-riga">
                                    <input
                                        type="checkbox"
                                        checked={righeStorno.includes(r.id)}
                                        onChange={(e) => setRigheStorno(
                                            e.target.checked
                                                ? [...righeStorno, r.id]
                                                : righeStorno.filter((x) => x !== r.id)
                                        )}
                                    />
                                    <span>{r.descrizione}</span>
                                    <b>{euro(r.totale)} €</b>
                                </label>
                            ))}
                        </div>
                        <input
                            className="ftz-motivo"
                            placeholder="Motivo (facoltativo)"
                            value={motivoStorno}
                            onChange={(e) => setMotivoStorno(e.target.value)}
                        />
                        <div className="ftz-modale-azioni">
                            <button className="ftz-btn" onClick={() => setNotaCredito(null)}>
                                Annulla
                            </button>
                            <button
                                className="ftz-btn ftz-btn--principale"
                                onClick={creaNotaCredito}
                                disabled={inCorso || !righeStorno.length}
                            >
                                Crea nota di credito
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- prestazioni da fatturare ---------- */}
            {!bozza && (
                <div className="ftz-card">
                    <div className="ftz-card-head">
                        <span>Prestazioni da fatturare</span>
                        {daFatturare.length > 0 && (
                            <button
                                className="ftz-btn ftz-btn--principale"
                                onClick={creaDocumento}
                                disabled={inCorso || !selezionate.length}
                            >
                                <Receipt size={16} /> Crea documento ({selezionate.length})
                            </button>
                        )}
                    </div>
                    {daFatturare.length === 0 ? (
                        <p className="ftz-vuoto">
                            Nessuna prestazione in attesa.{' '}
                            <button className="ftz-link" onClick={creaDocumento} disabled={inCorso}>
                                Crea comunque un documento
                            </button>
                        </p>
                    ) : (
                        <table className="ftz-tabella">
                            <tbody>
                                {daFatturare.map((p) => (
                                    <tr key={p.id}>
                                        <td className="ftz-sel">
                                            <input
                                                type="checkbox"
                                                checked={selezionate.includes(p.id)}
                                                onChange={(e) => setSelezionate(
                                                    e.target.checked
                                                        ? [...selezionate, p.id]
                                                        : selezionate.filter((x) => x !== p.id)
                                                )}
                                            />
                                        </td>
                                        <td>
                                            {p.descrizione_fattura_snapshot || p.nome_snapshot}
                                            <span className="ftz-data">{dataIta(p.data_visita)}</span>
                                            {/* Piu' prestazioni della stessa visita vanno
                                                nella stessa fattura: un clic le prende tutte. */}
                                            {(perVisita[p.visita_id] || []).length > 1 && (
                                                <button type="button" className="ftz-link"
                                                        onClick={() => setSelezionate((sel) => [...new Set([
                                                            ...sel, ...perVisita[p.visita_id]])])}>
                                                    tutta la visita ({perVisita[p.visita_id].length})
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {p.medico_cognome && (
                                                <span className="ftz-medico">
                                                    {p.medico_titolo} {p.medico_cognome}
                                                </span>
                                            )}
                                        </td>
                                        <td className="num">{euro(p.prezzo_unitario)} €</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ---------- pacchetti ---------- */}
            {(pacchetti.length > 0 || vende) && (
                <div className="ftz-card">
                    <div className="ftz-card-head">
                        <span><Package size={16} /> Pacchetti</span>
                        {vende && !vendita && (
                            <button className="ftz-btn" onClick={() => setVendita({
                                prestazione_id: '', descrizione: '', numero_sedute: '', prezzo_totale: '' })}>
                                <Plus size={14} /> Vendi pacchetto
                            </button>
                        )}
                    </div>
                    {vendita && (
                        <div className="ftz-pannello">
                            <div className="ftz-form-riga">
                                <select value={vendita.prestazione_id} onChange={(e) => {
                                    const v = catalogo.find((x) => String(x.id) === e.target.value);
                                    setVendita((x) => ({ ...x, prestazione_id: e.target.value,
                                        descrizione: x.descrizione || (v ? v.nome : '') }));
                                }}>
                                    <option value="">Voce del catalogo…</option>
                                    {catalogo.filter((v) => v.tipologia !== 'bene').map((v) => (
                                        <option key={v.id} value={v.id}>{v.nome}</option>
                                    ))}
                                </select>
                                <input placeholder="Descrizione del pacchetto" value={vendita.descrizione}
                                    onChange={(e) => setVendita((x) => ({ ...x, descrizione: e.target.value }))} />
                                <label className="ftz-qta">Sedute
                                    <input className="ftz-mini" type="number" min="1" step="1" value={vendita.numero_sedute}
                                        onChange={(e) => setVendita((x) => ({ ...x, numero_sedute: e.target.value }))} />
                                </label>
                                <label className="ftz-qta">Prezzo €
                                    <input className="ftz-mini" type="number" min="0.01" step="0.01" value={vendita.prezzo_totale}
                                        onChange={(e) => setVendita((x) => ({ ...x, prezzo_totale: e.target.value }))} />
                                </label>
                            </div>
                            <p className="ftz-nota">
                                Il prezzo è quello dell'intero pacchetto, IVA esclusa come il listino. Si fattura
                                dopo, anche a rate: la scadenza di dodici mesi parte dalla prima fattura emessa.
                            </p>
                            <div className="ftz-azioni">
                                <button className="ftz-btn ftz-btn--principale" disabled={inCorso
                                    || !vendita.descrizione || !vendita.numero_sedute || !vendita.prezzo_totale}
                                    onClick={vendiPacchetto}>
                                    <Check size={14} /> Registra la vendita
                                </button>
                                <button className="ftz-btn" onClick={() => setVendita(null)}>Annulla</button>
                            </div>
                        </div>
                    )}
                    {pacchetti.length === 0 && !vendita && <p className="ftz-vuoto">Nessun pacchetto</p>}
                    {pacchetti.length > 0 && (
                    <table className="ftz-tabella">
                        <tbody>
                            {pacchetti.map((p) => (
                                <tr key={p.id}>
                                    <td>
                                        {p.descrizione}
                                        <span className="ftz-data">
                                            {p.sedute_eseguite} di {p.numero_sedute} eseguite
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`ftz-stato ftz-stato--${p.stato}`}>{p.stato}</span>
                                    </td>
                                    <td className="num">
                                        {p.data_scadenza ? `scade ${dataIta(p.data_scadenza)}` : 'senza scadenza'}
                                    </td>
                                    <td className="num">
                                        {p.prezzo_totale !== undefined && p.fatturato !== undefined
                                            ? `fatturati ${euro(p.fatturato)} di ${euro(p.prezzo_totale)} €`
                                            : `${euro(p.valore_unitario)} € a seduta`}
                                    </td>
                                    <td className="num">
                                        {vende && p.stato === 'attivo' && Number(p.da_fatturare) > 0 && (
                                            <span className="ftz-pacchetto-fattura">
                                                <input className="ftz-mini" type="number" min="0.01" step="0.01"
                                                    placeholder={euro(p.da_fatturare)} title="Vuoto = tutto quello che resta"
                                                    value={importoPacchetto[p.id] ?? ''}
                                                    onChange={(e) => setImportoPacchetto((x) => ({ ...x, [p.id]: e.target.value }))} />
                                                <button className="ftz-btn" disabled={inCorso} onClick={() => fatturaPacchetto(p)}>
                                                    <Receipt size={14} /> Fattura
                                                </button>
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    )}
                </div>
            )}

            {/* ---------- documenti emessi ---------- */}
            <div className="ftz-card">
                <div className="ftz-card-head"><span>Documenti</span></div>
                {documentiTotale > documenti.length && (
                    <p className="ftz-vuoto">
                        Mostrati i {documenti.length} più recenti su {documentiTotale}: gli altri sono
                        nella pagina Fatturazione.
                    </p>
                )}
                {documenti.length === 0 ? (
                    <p className="ftz-vuoto">Nessun documento</p>
                ) : (
                    <table className="ftz-tabella">
                        <tbody>
                            {documenti.map((f) => (
                                <tr key={f.id}>
                                    <td>
                                        {f.numero ? `n. ${f.numero}/${f.anno}` : 'bozza'}
                                        <span className="ftz-data">{dataIta(f.data_documento)}</span>
                                    </td>
                                    <td>{f.intestatario}</td>
                                    <td className="num">{euro(f.totale)} €</td>
                                    <td className="num">
                                        {parseFloat(f.incassato) < parseFloat(f.totale) && (
                                            <span className="ftz-daincassare">
                                                da incassare {euro(f.totale - f.incassato)} €
                                            </span>
                                        )}
                                    </td>
                                    <td className="num">
                                        <button className="ftz-icona" onClick={() => ricaricaBozza(f.id)}
                                                title={f.stampata_il ? 'Apri' : 'Apri e modifica'}>
                                            <Receipt size={18} />
                                        </button>
                                        {f.numero && (
                                            <button className="ftz-icona" onClick={() => apriPdf(f.id)} title="Anteprima">
                                                <FileText size={18} />
                                            </button>
                                        )}
                                        {f.numero && !f.stampata_il && (
                                            <button className="ftz-icona" onClick={() => chiediStampa(f.id)}
                                                    title="Stampa definitiva">
                                                <Printer size={18} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default FatturazioneTab;
